import AddIcon from "@mui/icons-material/Add";
import { LoadingButton } from "@mui/lab";
import {
  Box,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import {
  AssetSource,
  AssetType,
  IAsset,
} from "../../../../../types/NendaTypes";
import { t } from "i18next";
import { CMSAsset } from "@client/models/CMS/cmsAsset";
import CmsAssetSearch from "./CmsAssetSearch";

type CmsAssetKind = AssetType.VIDEO | AssetType.LINEAR_CHANNEL;

interface AddEditAssetFormProps {
  onAdd: (asset: IAsset) => void;
  allowedAssetTypes?: AssetType[];
}

const AddEditAsset: React.FC<AddEditAssetFormProps> = ({
  onAdd,
  allowedAssetTypes = [AssetType.VIDEO, AssetType.LINEAR_CHANNEL],
}) => {
  const [type, setType] = useState<CmsAssetKind>(AssetType.VIDEO);
  const [selected, setSelected] = useState<CMSAsset | null>(null);
  const canAddLinearChannel = allowedAssetTypes.includes(
    AssetType.LINEAR_CHANNEL
  );

  const changeType = (next: CmsAssetKind) => {
    setType(next);
    setSelected(null);
  };

  const addAsset = () => {
    if (!selected) return;
    // Screens still read source "RedBee" as "a CMS asset"; do not rename it.
    onAdd({
      name: selected.getDisplayName(),
      locator: selected.assetId,
      type,
      source: AssetSource.REDBEE,
    } as IAsset);
    setSelected(null);
  };

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: 500,
        bgcolor: "background.paper",
        padding: "15px",
      }}
    >
      <Typography
        mt={2}
        mb={1}
        variant="h6"
        sx={{ display: "flex", justifyContent: "left" }}
      >
        {t("customerportal.pages.content_channels.add_asset")}
      </Typography>
      <Stack spacing={2}>
        <FormControl>
          <FormLabel id="add-asset-type-label">Type</FormLabel>
          <RadioGroup
            row
            aria-labelledby="add-asset-type-label"
            name="add-asset-type"
            value={type}
            onChange={(e) => changeType(e.target.value as CmsAssetKind)}
          >
            <FormControlLabel
              value={AssetType.VIDEO}
              control={<Radio />}
              label={t("customerportal.pages.content_channels.video")}
            />
            {canAddLinearChannel ? (
              <FormControlLabel
                value={AssetType.LINEAR_CHANNEL}
                control={<Radio />}
                label={t("customerportal.pages.content_channels.linear")}
              />
            ) : (
              <Tooltip title="A playlist may only contain one linear channel and then nothing else">
                <FormControlLabel
                  value={AssetType.LINEAR_CHANNEL}
                  control={<Radio />}
                  label={t("customerportal.pages.content_channels.linear")}
                  disabled={true}
                />
              </Tooltip>
            )}
          </RadioGroup>
        </FormControl>
        <CmsAssetSearch
          kind={type === AssetType.VIDEO ? "video" : "channel"}
          onSelect={setSelected}
        />
        {selected && (
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="body2"
              color="text.primary"
              sx={{ overflowWrap: "anywhere" }}
            >
              {selected.getDisplayName()}
            </Typography>
            <Typography variant="caption" color="text.primary">
              {selected.assetId}
            </Typography>
          </Box>
        )}
        <LoadingButton
          sx={{ mt: 1 }}
          disabled={!selected}
          onClick={addAsset}
          variant="contained"
          startIcon={<AddIcon />}
        >
          {t("customerportal.pages.content_channels.add_asset")}
        </LoadingButton>
      </Stack>
    </Box>
  );
};

export default AddEditAsset;
